import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Clock, Video, FileHeart, Play, Scale, Stethoscope } from "lucide-react";
import { ProviderChartPanel } from "@/components/MedicalRecords";

const countdown = (iso) => {
  const diff = new Date(iso) - Date.now();
  if (diff < -60 * 60 * 1000) return { label: "Past", tone: "text-slate-400" };
  if (diff < 0) return { label: "In progress window", tone: "text-emerald-600" };
  const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000);
  return { label: h > 0 ? `Arrives in ${h}h ${m}m` : `Arrives in ${m}m`, tone: m < 30 && h === 0 ? "text-amber-600 font-bold" : "text-cobalt" };
};

const STATUS_TONE = { visit_started: "bg-emerald-50 text-emerald-600", visit_completed: "bg-slate-100 text-slate-500", client_no_show: "bg-red-50 text-red-600" };

export default function TodaySchedule() {
  const [data, setData] = useState(null);
  const [chartFor, setChartFor] = useState(null);
  const [, tick] = useState(0);
  const load = useCallback(() => api.get("/telehealth/schedule/today").then(({ data }) => setData(data)).catch(() => setData({ appointments: [] })), []);
  useEffect(() => { load(); const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, [load]);

  const act = async (id, action) => {
    try { await api.post(`/telehealth/appointments/${id}/status`, { action }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  if (!data) return <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="th-today-schedule">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-navy">Today's Schedule</h2>
          <p className="text-xs text-slate-400">{data.date_label} · {data.timezone} · {data.count} appointment{data.count === 1 ? "" : "s"}</p>
        </div>
      </div>
      {!data.appointments.length ? (
        <div className="py-14 text-center text-sm text-slate-400" data-testid="th-today-empty">No appointments today. Confirmed appointments appear here automatically.</div>
      ) : (
        <div className="space-y-3">
          {data.appointments.map((a) => {
            const cd = countdown(a.start_utc);
            return (
              <div key={a.appointment_id} className="bg-white rounded-3xl border border-slate-100 p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`th-today-${a.appointment_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <div className="font-heading text-lg font-extrabold text-navy">{a.local_label}</div>
                    <div className={`text-[10px] ${cd.tone}`} data-testid={`th-countdown-${a.appointment_id}`}>{cd.label}</div>
                  </div>
                  <div className="border-l border-slate-100 pl-4">
                    <div className="font-semibold text-navy text-sm flex items-center gap-2">
                      {a.client_name}
                      {a.demo && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-cobalt/10 text-cobalt">Demo</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_TONE[a.status] || "bg-slate-100 text-slate-500"}`}>{a.status.replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.accident_type || a.type?.replace(/_/g, " ")} · {a.injury_type}</div>
                    <div className="flex gap-3 text-[11px] text-slate-400 mt-1">
                      <span className="flex items-center gap-1"><Scale className="w-3 h-3" /> {a.attorney}</span>
                      <span className="flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {a.rn}</span>
                      {a.forms_open > 0 && <span className="text-amber-600 font-semibold">{a.forms_open} form(s) pending</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-bold"><Video className="w-3.5 h-3.5" /> Join</a>}
                  {["appointment_confirmed", "forms_pending", "forms_complete", "ready_for_visit"].includes(a.status) &&
                    <button onClick={() => act(a.appointment_id, "start")} data-testid={`th-today-start-${a.appointment_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-white text-[11px] font-bold"><Play className="w-3.5 h-3.5" /> Start visit</button>}
                  {a.status === "visit_started" &&
                    <button onClick={() => act(a.appointment_id, "complete")} data-testid={`th-today-complete-${a.appointment_id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold">Complete visit</button>}
                  <button onClick={() => setChartFor(a)} data-testid={`th-today-case-${a.appointment_id}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-[11px] font-bold"><FileHeart className="w-3.5 h-3.5" /> Open Case</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {chartFor && <ProviderChartPanel appointment={chartFor} onClose={() => { setChartFor(null); load(); }} />}
    </div>
  );
}
